import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { login, isMFARequired, generateDeviceFingerprint } from "../services/authService";
import { Footer } from "../components/layout/Footer";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth, setMFAPending } = useAuthStore();
  
  // Check if we came here from session expiration
  const locationState = location.state as { returnUrl?: string; sessionExpired?: boolean } | null;
  const returnUrl = locationState?.returnUrl;
  const sessionExpired = locationState?.sessionExpired;

  // Show session expired message if applicable
  useEffect(() => {
    if (sessionExpired) {
      setError("Your session has expired. Please log in again.");
    }
  }, [sessionExpired]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Generate device fingerprint for trusted device check
      const deviceFingerprint = await generateDeviceFingerprint();
      const response = await login(email, password, deviceFingerprint);

      if (isMFARequired(response)) {
        // MFA is required, store partial auth and navigate to MFA verification page
        setMFAPending(
          response.session_id,
          response.mfa_method,
          response.access_token,
          response.refresh_token,
        );
        navigate("/mfa-verify");
      } else {
        // Login successful
        setAuth(
          response.access_token,
          response.refresh_token,
          response.expires_at,
          response.user,
        );
        // Navigate to return URL if present, otherwise default to search
        navigate(returnUrl || "/search");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
    <div className="flex-1 flex animated-gradient">
      {/* Left side - Branding/Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="/header.mp4?v=2" type="video/mp4" />
        </video>

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/50" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12">
          {/* Colvix logo */}
          <a
            href="https://www.colvix.net"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
          >
            <img src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8" />
          </a>

          {/* TradeCraft branding */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/logo.svg"
                alt="TradeCraft"
                className="w-16 h-16 drop-shadow-lg"
              />
              <div>
                <h1 className="text-4xl font-bold gradient-text">
                  TradeCraft
                </h1>
                <p className="text-surface-400 text-lg">Algo Builder</p>
              </div>
            </div>
          </div>

          {/* Features list */}
          <div className="space-y-4">
            {[
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Comprehensive Backtesting",
                desc: "Test strategies with historical data",
              },
              {
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                title: "Performance Analytics",
                desc: "Sharpe ratio, drawdown, and more",
              },
              {
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Portfolio Management",
                desc: "Track your investment strategies",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={feature.icon}
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-surface-100">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-surface-400">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/colvix_header_final.png"
                alt="Colvix"
                className="colvix-logo h-6 mx-auto"
              />
            </a>
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/logo.svg" alt="TradeCraft" className="w-12 h-12" />
              <h1 className="text-2xl font-bold gradient-text">
                TradeCraft
              </h1>
            </div>
            <p className="text-surface-400">Algo Builder</p>
          </div>

          {/* Login card */}
          <div className="glass-card p-8 animate-slide-up">
            {/* Login Form */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-surface-100">
                Welcome back
              </h2>
              <p className="text-surface-400 mt-2">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg
                      className="w-5 h-5 text-surface-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field w-full pl-12"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-surface-300">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg
                      className="w-5 h-5 text-surface-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field w-full pl-12"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full py-3 text-base relative overflow-hidden group"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="spinner" />
                    Signing in...
                  </span>
                ) : (
                  <>
                    <span className="relative z-10">Sign In</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </>
                )}
              </button>

              <div className="text-center mt-6">
                <p className="text-surface-400">
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
                  >
                    Create one
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
